import React from "react";
import { useAppSelector, useAppTranslation } from "@/utils";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, LegacyDatePicker, LegacyForm, Space, Typography } from "@payping/web-components";
import { DashboardChecksTypeEnum } from "@payping/api";
import { Close } from "@payping/icons";
import EditActionButtons from "@/components/mobile/settings/EditActionButtons";
import { dateFormats, isBeforeToday, toJalali, toUtc } from "@payping/utils/dateUtils";
import type { SDKDateObject } from "@payping/utils/dateUtils";
import { DashboardRoutes } from "@/router/constant";
import { isMobileBusinessRegisterURL } from "@/utils/isRegisterURL";
import { useHandleNavigationURL } from "@/mobile/Settings/Business/utils";
import { useImmerAtom } from "jotai-immer";
import { settingsBusinessAtom } from "@/mobile/Settings/state";
import { useAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { Trans } from "@payping/utils/localeUtils";

const mobileFormEditAtom = selectAtom(settingsBusinessAtom, (val) => val.mobileFormEdit);

const MobileEditOfficialBusinessRegistrationDate: React.FC = () => {
  const isRegisterURL = isMobileBusinessRegisterURL();
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [form] = LegacyForm.useForm();
  const checks = useAppSelector((state) => state.user.checks);
  const isSabtDisabled = checks?.businessRegistrationStatus === DashboardChecksTypeEnum.Confirmed;
  const [mobileFormEditState] = useAtom(mobileFormEditAtom);

  const registrationDateApi = useAppSelector((state) => state.user.businessInfo?.registrationDate);

  const [, setSettingsBusinessState] = useImmerAtom(settingsBusinessAtom);

  const utcDateApi =
    registrationDateApi && registrationDateApi !== "0001-01-01T00:00:00"
      ? (toJalali({
          convertTime: false,
          date: registrationDateApi,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.standard.utcDate,
        }).object as SDKDateObject)
      : undefined;
  const utcDateRedux =
    mobileFormEditState.registrationDate && mobileFormEditState.registrationDate !== "0001-01-01T00:00:00"
      ? (toJalali({
          convertTime: false,
          date: mobileFormEditState.registrationDate,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.standard.utcDate,
        }).object as SDKDateObject)
      : undefined;

  function saveToRedux(values: { registrationDate?: SDKDateObject }) {
    const registrationDate = toUtc({
      convertTime: false,
      date: values.registrationDate,
      toFormat: dateFormats.standard.utcDate,
    }).string;
    setSettingsBusinessState((draft) => {
      // eslint-disable-next-line no-param-reassign
      draft.mobileFormEdit.registrationDate = registrationDate;
    });
    if (isRegisterURL) return navigate(DashboardRoutes.register.mEditBusiness.officialBusinessbNationalCode.url);
    return navigate(DashboardRoutes.settings.mEditBusiness.officialBusinessbNationalCode.url);
  }

  // Custom Hooks
  const { handleNavigationURL } = useHandleNavigationURL();

  return (
    <>
      <Drawer
        placement={"bottom"}
        open={true}
        height={"100%"}
        title={`${t("edit")} / ${t("settings.business.registrationDate")}`}
        closeIcon={false}
        extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleNavigationURL} />}
      >
        <Space orientation={"vertical"}>
          <LegacyForm
            onFinish={saveToRedux}
            form={form}
            initialValues={{ registrationDate: utcDateRedux ?? utcDateApi }}
          >
            <LegacyForm.Item
              label={t("settings.business.registrationDate")}
              name="registrationDate"
              rules={[{ required: true }]}
            >
              <LegacyDatePicker
                className={"tw:w-full"}
                disabledDate={(date) => isBeforeToday(date)}
                disabled={!form.getFieldValue("registrationDate") ? false : isSabtDisabled}
              />
            </LegacyForm.Item>
          </LegacyForm>
          <Typography.Text className="tw:whitespace-pre-line" type={"secondary"}>
            <Trans
              i18nKey={"mobileEditPersonal.nameDesc"}
              components={[
                <Typography.Link
                  key={0}
                  href={"https://irsherkat.ssaa.ir/Design/SearchCompanyPublicInfo.aspx"}
                  target="_blank"
                />,
              ]}
            />
          </Typography.Text>
        </Space>
      </Drawer>
      <EditActionButtons onAccept={form.submit} acceptText={t("next")} />
    </>
  );
};

export default MobileEditOfficialBusinessRegistrationDate;
